import { db } from './database'

/**
 * Les marqueurs locaux : ils pilotent des écrans, ils ne décrivent aucune
 * donnée d'argent. Jamais synchronisés — un second appareil peut reposer la
 * question, ce qui est bénin.
 *
 * Ils vivent dans leur propre module parce que `db/budget.ts` et
 * `db/onboarding.ts` en ont besoin tous les deux, et que ces deux-là
 * s'importent déjà dans un sens : les loger dans l'un ou l'autre fermerait un
 * cycle d'imports.
 */

/** Ce que l'utilisateur a demandé en reposant les questions du premier lancement. */
export type RedoMode =
  /** Corriger le rythme et le montant. Tout le reste est conservé. */
  | 'settings'
  /** Recommencer la période en cours : opérations, enveloppes, journées prévues. */
  | 'cycle'

const redoKey = (walletId: string) => `redo_onboarding:${walletId}`
const incomeKey = (walletId: string) => `onboarding_income:${walletId}`

export async function markRedo(walletId: string, mode: RedoMode): Promise<void> {
  await db.meta.put({ key: redoKey(walletId), value: mode })
}

/** Le mode demandé, ou `null` si aucune reprise n'est en attente. */
export async function pendingRedo(walletId: string): Promise<RedoMode | null> {
  const mark = await db.meta.get(redoKey(walletId))
  if (!mark) return null
  // Un marqueur écrit avant que le mode existe ne porte pas de valeur utile :
  // la reprise la plus prudente est celle qui n'efface rien.
  return mark.value === 'cycle' ? 'cycle' : 'settings'
}

export async function clearRedo(walletId: string): Promise<void> {
  await db.meta.delete(redoKey(walletId))
}

/**
 * Retenir quel revenu ouvre le cycle en cours.
 *
 * Posé par le premier lancement ET par la clôture de cycle : les deux jouent le
 * même rôle — l'argent que l'app inscrit au démarrage d'un cycle. Corriger le
 * réglage doit pouvoir REMPLACER celui qui est en place, sinon le nouveau
 * montant s'ajoute à l'ancien et le solde grossit d'un argent qui n'existe pas.
 */
export async function rememberCycleIncome(walletId: string, id: string): Promise<void> {
  await db.meta.put({ key: incomeKey(walletId), value: id })
}

export async function cycleIncomeId(walletId: string): Promise<string | null> {
  return ((await db.meta.get(incomeKey(walletId)))?.value as string | undefined) ?? null
}

export async function forgetCycleIncome(walletId: string): Promise<void> {
  await db.meta.delete(incomeKey(walletId))
}
